"use client";

import { useEffect, useState } from "react";
import { signInAgent, requestAgentPasswordReset } from "@aluume/shared/lib/auth/agent";
import { SiteHeader } from "@/components/SiteHeader";

const INPUT = "border-b border-ink/20 bg-transparent pb-2 text-[15px] text-ink-soft outline-none focus:border-ink w-full";

const LINK_ERRORS: Record<string, string> = {
  link_invalid: "That link is invalid. Request a new password reset below.",
  link_expired: "That link has expired. Request a new password reset below.",
};

// Agent portal login — email/password. Uses the customer-facing SiteHeader
// pre-login (Jeremy: "keep the header from the customer-facing side until logged in").
export default function AgentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  // Surface a failed/expired email-link bounce from /auth/callback, pre-fill the
  // email + temp password from the welcome-email login link, and auto-submit.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("error");
    if (code && LINK_ERRORS[code]) setError(LINK_ERRORS[code]);
    const e = params.get("email");
    const pw = params.get("pw");
    if (e) setEmail(e);
    if (pw) setPassword(pw);
    // Arrived from the welcome email with both credentials → sign in automatically.
    if (e && pw && !code) void signInWith(e, pw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function redirect() {
    const params = new URLSearchParams(window.location.search);
    window.location.assign(params.get("next") ?? "/agent");
  }

  async function signInWith(emailArg: string, pwArg: string) {
    setBusy(true); setError(""); setNotice("");
    try {
      const session = await signInAgent(emailArg, pwArg);
      // Temp password (set at account creation) → force a password change first.
      if (session?.user?.user_metadata?.must_change_password) {
        window.location.assign("/agent/reset?forced=1");
        return;
      }
      redirect();
    } catch (e) {
      setError((e as Error).message || "Couldn't sign in — check your details and try again.");
      setBusy(false);
    }
  }

  function signIn() { void signInWith(email, password); }

  async function forgot() {
    if (!email) { setError("Enter your email first, then tap Forgot password."); return; }
    try {
      await requestAgentPasswordReset(email);
      setNotice("If that email has an account, a reset link is on its way.");
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-cream">
      <SiteHeader />

      <div className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="flex w-full max-w-[400px] flex-col">
          <h3 className="mb-2 text-center">Welcome back</h3>
          <p className="body mb-9 text-center text-ink-soft">
            Sign in to book on account and view your statements.
          </p>

          <form
            className="flex flex-col gap-5"
            onSubmit={(e) => { e.preventDefault(); if (!busy && email && password) signIn(); }}
          >
            <label className="flex flex-col gap-1.5">
              <span className="ui-label text-ink/50">Email</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@youragency.co.nz"
                className={INPUT}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="ui-label text-ink/50">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className={INPUT}
              />
            </label>

            <button
              type="submit"
              disabled={busy || !email || !password}
              className="ui-label mt-3 rounded-pill bg-ink py-3.5 text-ink-on-dark transition-colors hover:bg-accent disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <button
            type="button"
            onClick={forgot}
            className="caption mt-4 text-center text-ink/65 underline-offset-2 hover:underline"
          >
            Forgot password?
          </button>

          {error && <p className="caption mt-4 text-center text-corten">{error}</p>}
          {notice && <p className="caption mt-4 text-center text-ink-soft">{notice}</p>}

          <p className="caption mt-11 text-center text-ink-soft/80">
            No account? Contact Aluume reservations to be set up.
          </p>
        </div>
      </div>
    </main>
  );
}
